import React, { useEffect, useState, Component } from 'react';
import { Box, Card, CardContent, TextField, Button, Typography, FormControlLabel, Checkbox, Link, InputAdornment, IconButton, useMediaQuery, useTheme, Alert, LinearProgress } from '@mui/material';
import { Icon } from '@iconify/react';
import { useNavigate, useLocation } from 'react-router-dom';
interface LoginProps {
  onLogin: (username: string, password: string) => boolean;
}
export const Login: React.FC<LoginProps> = ({
  onLogin
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [loading, setLoading] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  // Check for success messages from redirect
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('reset') === 'success') {
      setSuccessMsg('Password successfully reset. Please sign in with your new password.');
    } else if (searchParams.get('changed') === 'success') {
      setSuccessMsg('Password successfully changed. Please sign in again.');
    }
  }, [location]);
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    setSuccessMsg('');
    if (!username.trim() || !password.trim()) {
      setError('Please enter both username and password');
      return;
    }
    setLoading(true);
    await new Promise(resolve => setTimeout(resolve, 800));
    const success = onLogin(username, password);
    if (!success) {
      setError('Invalid username or password');
      setLoading(false);
    }
  };
  return <Box sx={{
    display: 'flex',
    height: '100vh',
    overflow: 'hidden',
    position: 'relative'
  }} data-id="element-1596">
      {!isMobile && <Box sx={{
      flex: 1,
      backgroundColor: '#FFFFFF',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      padding: 4,
      overflow: 'hidden',
      position: 'relative',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '3px',
        backgroundColor: '#0052CC',
        zIndex: 10
      }
    }} data-id="element-1597">
          <Box component="img" src="https://uploadthingy.s3.us-west-1.amazonaws.com/2TFKkyaUakWdHzgEBHAz75/openRemitLogo-1.png" alt="OpenRemit Logo" sx={{
        height: 60,
        mb: 3
      }} data-id="element-1598" />
          <Typography variant="h4" component="h1" fontWeight="700" gutterBottom color="#1F2937" sx={{
        mb: 2
      }} data-id="element-1599">
            Simplify Cross-Border Payments
          </Typography>
          <Typography variant="body1" sx={{
        maxWidth: 450,
        textAlign: 'center',
        color: 'text.secondary',
        lineHeight: 1.6
      }} data-id="element-1600">
            Manage remittances, track transactions, and ensure compliance all
            from one powerful dashboard.
          </Typography>
          <Box component="img" src="https://img.freepik.com/free-vector/global-money-transfer-abstract-concept-illustration_335657-3893.jpg" alt="Global Payments Illustration" sx={{
        width: '70%',
        maxWidth: 400,
        mt: 4,
        filter: 'none',
        transition: 'none',
        animation: 'none'
      }} data-id="element-1601" />
        </Box>}
      <Box sx={{
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      padding: 2,
      backgroundColor: '#0385FB',
      overflow: 'auto',
      position: 'relative'
    }} data-id="element-1602">
        <Card sx={{
        maxWidth: 400,
        width: '100%',
        py: 1.5,
        position: 'relative',
        zIndex: 2,
        overflow: 'visible',
        backgroundColor: '#FFFFFF',
        boxShadow: '0px 4px 12px rgba(0,0,0,0.05)',
        border: '1px solid #E5E7EB',
        borderRadius: '8px',
        '&::before': {
          display: 'none'
        },
        '&:hover::before': {
          display: 'none'
        }
      }} elevation={0} data-id="element-1603">
          {loading && <LinearProgress sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16
        }} data-id="element-1604" />}
          <CardContent sx={{
          padding: {
            xs: 2.5,
            sm: 3.5
          },
          backgroundColor: '#FFFFFF'
        }} data-id="element-1605">
            <Box sx={{
            textAlign: 'center',
            mb: 3
          }} data-id="element-1606">
              {isMobile && <Box component="img" src="https://uploadthingy.s3.us-west-1.amazonaws.com/2TFKkyaUakWdHzgEBHAz75/openRemitLogo-1.png" alt="OpenRemit Logo" sx={{
              height: 50,
              mb: 1.5,
              filter: 'none'
            }} data-id="element-1607" />}
              <Typography variant="h5" component="h1" fontWeight="700" color="#1F2937" data-id="element-1608">
                Welcome to OpenRemit
              </Typography>
              <Typography variant="body2" color="#6B7280" mt={0.5} data-id="element-1609">
                Sign in to access your dashboard
              </Typography>
              <Box sx={{
              mt: 1.5,
              p: 1,
              backgroundColor: '#FEF3C7',
              borderRadius: 1,
              border: '1px solid #FCD34D'
            }} data-id="element-1610">
                <Typography variant="caption" color="#92400E" fontWeight={600} data-id="element-1611">
                  Demo: admin / test@1234
                </Typography>
              </Box>
            </Box>

            {successMsg && <Alert severity="success" sx={{
            mb: 2,
            fontSize: '0.8rem',
            borderRadius: 2
          }} onClose={() => setSuccessMsg('')} data-id="element-1612">
                {successMsg}
              </Alert>}

            {error && <Alert severity="error" sx={{
            mb: 1.5,
            fontSize: '0.8rem',
            borderRadius: 2,
            animation: 'shake 0.5s ease-in-out',
            '@keyframes shake': {
              '0%, 100%': {
                transform: 'translateX(0)'
              },
              '10%, 30%, 50%, 70%, 90%': {
                transform: 'translateX(-5px)'
              },
              '20%, 40%, 60%, 80%': {
                transform: 'translateX(5px)'
              }
            }
          }} onClose={() => setError('')} data-id="element-1613">
                {error}
              </Alert>}
            <Box component="form" onSubmit={handleSubmit} data-id="element-1614">
              <TextField label="Username" variant="outlined" fullWidth margin="normal" value={username} onChange={e => setUsername(e.target.value)} required size="small" sx={{
              mb: 1.5,
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#FFFFFF',
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#0052CC',
                  borderWidth: 2
                }
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: '#0052CC'
              }
            }} disabled={loading} data-id="element-1615" />
              <TextField label="Password" type={showPassword ? 'text' : 'password'} variant="outlined" fullWidth margin="normal" value={password} onChange={e => setPassword(e.target.value)} required size="small" InputProps={{
              endAdornment: <InputAdornment position="end" data-id="element-1617">
                      <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" size="small" disabled={loading} data-id="element-1618">
                        {showPassword ? <Icon icon="lucide:eye-off" width={18} height={18} data-id="element-1619" /> : <Icon icon="lucide:eye" width={18} height={18} data-id="element-1620" />}
                      </IconButton>
                    </InputAdornment>
            }} sx={{
              mb: 0.5,
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#FFFFFF',
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#0052CC',
                  borderWidth: 2
                }
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: '#0052CC'
              }
            }} disabled={loading} data-id="element-1616" />
              <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mt: 0.5,
              mb: 2
            }} data-id="element-1621">
                <FormControlLabel control={<Checkbox size="small" sx={{
                '&.Mui-checked': {
                  color: '#0052CC'
                }
              }} disabled={loading} data-id="element-1623" />} label={<Typography variant="body2" data-id="element-1624">Remember me</Typography>} data-id="element-1622" />
              </Box>
              <Button type="submit" variant="contained" fullWidth sx={{
              py: 1.2,
              fontSize: '0.875rem',
              position: 'relative',
              overflow: 'hidden',
              backgroundColor: '#0052CC',
              borderRadius: '6px',
              '&:hover': {
                backgroundColor: '#003DA8'
              }
            }} disabled={loading} data-id="element-1625">
                {loading ? 'Signing in...' : 'Sign In'}
                {loading && <Box sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(255,255,255,0.2)'
              }} data-id="element-1626">
                    <Box sx={{
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  border: '3px solid rgba(255,255,255,0.3)',
                  borderTopColor: '#fff',
                  animation: 'spin 1s linear infinite',
                  '@keyframes spin': {
                    to: {
                      transform: 'rotate(360deg)'
                    }
                  }
                }} data-id="element-1627" />
                  </Box>}
              </Button>

              <Box sx={{
              mt: 3,
              textAlign: 'center'
            }} data-id="element-1628">
                <Typography variant="body2" color="#6B7280" sx={{
                mb: 1
              }} data-id="element-1629">
                  New to Ecobank Partner Network?
                </Typography>
                <Button variant="outlined" fullWidth onClick={() => navigate('/onboarding')} sx={{
                py: 1.2,
                fontSize: '0.875rem',
                borderColor: '#E5E7EB',
                color: '#1F2937',
                borderRadius: '6px',
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: '#F3F5F7',
                  borderColor: '#D1D5DB'
                }
              }} disabled={loading} data-id="element-1630">
                  Register / Onboard
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
        <Typography variant="body2" color="#FFFFFF" sx={{
        mt: 3,
        fontSize: '0.75rem',
        fontWeight: 500,
        position: 'relative',
        zIndex: 2
      }} data-id="element-1631">
          Powered by Paysys Labs © 2026
        </Typography>
      </Box>
    </Box>;
};
export default Login;